import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Page, SiteSettings } from "../types";
import SearchBar from "./SearchBar";
import { ShieldCheck, Smartphone, Clock, Star, Heart, Quote, ChevronLeft, ChevronRight, X, Building2, Wallet } from 'lucide-react';
import api from '../utils/api';

// --- IMPORT LEAFLET ---
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix per le icone di default di Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const createPriceIcon = (price: any) => {
    let label = 'N/A';

    if (price !== undefined && price !== null) {
        const numPrice = Number(price);
        if (!isNaN(numPrice)) {
            label = '$' + numPrice.toLocaleString('en-US', {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
            });
        }
    }

    return L.divIcon({
        className: 'custom-price-marker',
        html: `<div style="
            background-color: white;
            color: #1F2A33;
            font-weight: 800;
            padding: 3px 5px;
            border-radius: 8px;
            box-shadow: 0 4px 10px rgba(0,0,0,0.2);
            border: 1px solid rgba(0,0,0,0.05);
            font-size: 12px;
            font-family: sans-serif;
            text-align: center;
            white-space: nowrap;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 30px;
            transform: translate(-50%, -50%);
        ">${label}</div>`,
        iconSize: [50, 30],
        iconAnchor: [25, 15]
    });
};

// Interfaccia per i dati dal Backend
interface TestimonialData {
    id: number;
    authorName: string;
    authorRole: string;
    content: string;
    rating: number;
    // Campi calcolati lato frontend per compatibilità
    preview?: string;
    fullText?: string;
}

interface HomeContentProps {
    navigate: (p: Page) => void;
    onCategorySelect: (category: string) => void;
    searchParams: any;
    setSearchParams: any;
    onSearch: () => void;
}

const HomeContent = ({ navigate, onCategorySelect, searchParams, setSearchParams, onSearch }: HomeContentProps) => {
    const [allProperties, setAllProperties] = useState<any[]>([]);
    const [settings, setSettings] = useState<SiteSettings>({});
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const heroImages = ["/Rental1.webp", "/Rental2.webp"];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentImageIndex((prev) => (prev === 0 ? 1 : 0));
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    // 1. STATO PER I TESTIMONIALS REALI
    const [testimonials, setTestimonials] = useState<TestimonialData[]>([]);

    const [loading, setLoading] = useState(true);

    // --- STATES FOR TESTIMONIALS ---
    const [selectedTestimonial, setSelectedTestimonial] = useState<TestimonialData | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Palette Colors Constants
    const BRAND_CORAL = "#E84C5B";

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 2. AGGIUNTA LA CHIAMATA PER I TESTIMONIALS
                const [propRes, setRes, testRes] = await Promise.all([
                    api.get('/properties'),
                    api.get('/content/settings'),
                    api.get('/content/testimonials')
                ]);

                if (propRes.data.success) {
                    setAllProperties(propRes.data.data);
                }
                if (setRes.data.success) setSettings(setRes.data.data);

                const mappedTestimonials = testRes.data.data.map((t: any) => ({
                    id: t.id,
                    rating: t.rating,
                    // Mappiamo "name" del JSON su "authorName" del componente
                    authorName: t.name || "Anonymous",
                    // Mappiamo "role" del JSON su "authorRole" del componente
                    authorRole: t.role || "Guest",
                    // Mappiamo "fullText" del JSON direttamente
                    fullText: t.fullText || "",
                    // Creiamo la preview basandoci su fullText
                    preview: (t.fullText && t.fullText.length > 120)
                        ? t.fullText.substring(0, 120) + "..."
                        : (t.fullText || "")
                }));
                setTestimonials(mappedTestimonials);

            } catch (error) {
                console.error("Error fetching home data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const defaultCenter: [number, number] = [40.90, -72.35];

    // Scroll Handlers
    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 340;
            const currentScroll = scrollContainerRef.current.scrollLeft;
            scrollContainerRef.current.scrollTo({
                left: direction === 'right' ? currentScroll + scrollAmount : currentScroll - scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="animate-fade-in w-full overflow-hidden relative">

            {/* HERO SECTION */}
            <div className="relative h-[80vh] md:h-[85vh] w-full rounded-b-[2rem] md:rounded-b-[3.5rem] overflow-hidden shadow-2xl z-0 bg-black">
                {heroImages.map((src, index) => (
                    <img
                        key={`${src}-${index === currentImageIndex}`} // La key cambia per resettare l'animazione
                        src={src}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out ${
                            index === currentImageIndex
                                ? "opacity-100 animate-zoom-in z-0"
                                : "opacity-0 z-0"
                        }`}
                        alt="Luxury Rental"
                    />
                ))}

                {/* Overlay per leggibilità testo */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/20 to-black/70 md:to-black/60 z-10"></div>

                {/* Contenuto Testuale */}
                <div className="absolute bottom-24 md:bottom-40 left-0 right-0 text-center text-white px-4 md:px-6 z-20">
                    <h1 className="text-4xl md:text-7xl font-serif font-bold mb-3 md:mb-6 drop-shadow-xl leading-tight tracking-tight">
                        Feel at home.<br/>Everywhere.
                    </h1>
                    <p className="text-base md:text-xl font-light mb-6 md:mb-10 max-w-2xl mx-auto drop-shadow-md text-gray-100 px-4">
                        Your Smart Partner for unique hospitality experiences. <br className="hidden md:block"/>
                        From exclusive estates to smart city apartments.
                    </p>
                    <button
                        onClick={() => navigate('villas')}
                        className="bg-white text-gray-900 px-8 py-3 md:px-10 md:py-4 rounded-full font-bold text-sm md:text-lg hover:bg-brand hover:text-white transition-all transform hover:scale-105 shadow-xl active:scale-95"
                    >
                        Explore Properties
                    </button>
                </div>
            </div>

            {/* --- SEARCH BAR --- (Invariato) */}
            <div className="px-4 md:px-6 relative z-20 -mt-10 md:-mt-12">
                <SearchBar params={searchParams} setParams={setSearchParams} onSearch={onSearch} />
            </div>

            <div className="h-16 md:h-24"></div>

            {/* --- MAP SECTION --- (Invariato) */}
            <section className="max-w-[1600px] mx-auto px-4 md:px-6 mb-16 md:mb-24">
                <div className="text-center mb-10">
                    <span className="text-brand font-bold text-xs md:text-sm tracking-widest uppercase">Explore the Area</span>
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mt-2">Our Locations</h2>
                </div>
                <style>{`.leaflet-tile-pane { filter: saturate(0.6) contrast(1.1) brightness(1.02); }`}</style>
                <div className="w-full h-[400px] md:h-[450px] rounded-[2rem] md:rounded-[3rem] overflow-hidden shadow-xl border border-gray-200 relative z-0">
                    {!loading && (
                        <MapContainer center={defaultCenter} zoom={11} scrollWheelZoom={false} className="w-full h-full z-0" style={{ height: "100%", width: "100%", zIndex: 0 }}>
                            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>' url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY ?? ''}`} />
                            {allProperties.map((property) => {
                                if (property.lat && property.lng) {
                                    return (
                                        <Marker key={property.id} position={[property.lat, property.lng]} icon={createPriceIcon(property.price)}>
                                            <Popup className="custom-popup">
                                                <div className="w-52 p-1 font-sans">
                                                    <div className="aspect-video w-full rounded-lg overflow-hidden mb-2">
                                                        <img src={property.image} alt={property.title} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=No+Image'; }} />
                                                    </div>
                                                    <h3 className="font-bold text-gray-900 text-sm mb-1 leading-tight">{property.title}</h3>
                                                    <div className="flex justify-between items-center mt-2">
                                                        <span className="font-bold text-brand text-sm">{property.price ? `$${Number(property.price).toLocaleString()}` : 'N/A'}</span>
                                                        <button onClick={() => navigate(`details/${property.id}?guest=1` as any)} className="bg-gray-900 text-white text-[10px] px-3 py-1.5 rounded-md font-bold hover:bg-brand transition-colors">Details</button>
                                                    </div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    );
                                }
                                return null;
                            })}
                        </MapContainer>
                    )}
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-4 md:px-6 mb-24 md:mb-32">
                <div className="flex flex-col md:flex-row items-center gap-10 md:gap-20">

                    {/* Immagine (A sinistra su desktop, sopra su mobile) */}
                    <div className="flex-1 w-full relative order-1 md:order-1">
                        <div className="absolute -top-10 -left-10 w-40 h-40 bg-gray-100 rounded-full blur-3xl -z-10"></div>
                        <div className="relative h-[400px] md:h-[600px] w-full rounded-[2rem] overflow-hidden shadow-2xl">
                            {/* Inserisci qui il link all'immagine reale del golf o della villa che preferisci */}
                            <img
                                src="img/film-festival.jpg"
                                alt="Hamptons International Film Festival 2026"
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.5s] ease-out"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                        </div>
                    </div>

                    {/* Testo (A destra su desktop, sotto su mobile) */}
                    <div className="flex-1 text-left order-2 md:order-2">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-8 h-[1px] bg-brand"></span>
                            <span className="text-brand font-bold text-xs tracking-[0.2em] uppercase">
                                Featured Event
                            </span>
                        </div>

                        <h2 className="text-3xl md:text-5xl font-serif text-gray-900 mb-8 leading-[1.2]">
                            Hamptons International Film Festival 2026 <br className="hidden lg:block" />
                            <span className="italic text-gray-500">– Stay with Another Home</span>
                        </h2>

                        <div className="text-lg text-gray-600 font-light leading-relaxed mb-10 max-w-xl space-y-6">
                            <p>
                                From <strong>October 2–12, 2026</strong>, the <strong>Hamptons International Film Festival</strong> returns for its 34th edition, bringing eleven days of exceptional cinema, filmmaker conversations, premieres, and special events to <strong>East Hampton</strong>, <strong>Sag Harbor</strong>, and <strong>Southampton</strong>.
                            </p>
                            <p>
                                For this celebrated event, <strong>Another Home</strong> offers a curated collection of exceptional private villas throughout the <strong>Hamptons</strong> — ideal for guests looking to combine the excitement of the festival with the privacy, comfort, and character of a private home.
                            </p>
                            <p>
                                Experience the festival in true Hamptons style: attend screenings and special events by day, then return to a beautifully appointed home with generous living spaces, gardens, pools, and the freedom to enjoy your stay at your own pace.
                            </p>
                            <p>
                                Whether you are attending as a film enthusiast, industry professional, couple, family, or group of friends, <strong>Another Home</strong> provides the perfect base for experiencing one of the most distinctive cultural events on the East End.
                            </p>
                        </div>

                    </div>

                </div>
            </section>

            {/* --- PHILOSOPHY BOX --- (Invariato)
            <section className="max-w-6xl mx-auto px-4 md:px-6 mb-16 md:mb-24">
                <div className="rounded-[2rem] md:rounded-[3rem] p-8 md:p-16 text-black text-center shadow-2xl relative overflow-hidden" style={{backgroundColor: "#D6D1CB"}}>
                    <div className="relative z-10">
                        <div className="bg-white w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto mb-6 md:mb-8 backdrop-blur-md">
                            <Heart className="w-6 h-6 md:w-8 md:h-8 fill-brand text-white" />
                        </div>
                        <h3 className="text-2xl md:text-3xl font-serif font-bold mb-4 md:mb-6">A New Way of Staying</h3>
                        <p className="text-lg md:text-2xl opacity-95 max-w-4xl mx-auto leading-relaxed font-light italic">
                            "We don't just offer a place to sleep, but another home to live in. We unite smart technology with the warmth of modern hospitality."
                        </p>
                    </div>
                    <div className="absolute top-0 left-0 w-40 h-40 md:w-64 md:h-64 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-40 h-40 md:w-64 md:h-64 bg-white/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
                </div>
            </section>*/}

            {/* --- FOR OWNER SECTION --- (Invariato) */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-6 mb-24 md:mb-32">
                <div className="flex flex-col md:flex-row items-center gap-10 md:gap-20">
                    <div className="flex-1 text-left order-2 md:order-1">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-8 h-[1px] bg-brand"></span>
                            <span className="text-brand font-bold text-xs tracking-[0.2em] uppercase">
                                For Owners
                            </span>
                        </div>

                        <h2 className="text-3xl md:text-5xl font-serif text-gray-900 mb-8 leading-[1.2]">
                            Co‑Hosting & Hospitality  <br className="hidden lg:block" />
                            <span className="italic text-gray-500">for Exceptional Homes in the Hamptons </span>
                        </h2>

                        <div className="text-lg text-gray-600 font-light leading-relaxed mb-10 max-w-xl space-y-6">
                            <p>
                                Your Hamptons home is more than a seasonal retreat.
                                It is a private asset that deserves thoughtful positioning, , careful oversight, and hospitality standards aligned with its value.
                            </p>
                            <p>
                                At <strong className="text-gray-900 font-medium">Another Home</strong>, we provide co‑hosting and hospitality operations for luxury second homes, supporting platform‑facilitated short‑term guest stays with a focus on care, discretion, and long‑term asset integrity.
                            </p>
                        </div>

                        <button
                            onClick={() => navigate('owners' as any)}
                            className="group inline-flex items-center gap-3 text-brand  font-bold text-sm tracking-widest uppercase border-b border-gray-200 pb-2 hover:border-brand hover:text-brand transition-all"
                        >
                            Read More
                            <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                    <div className="flex-1 w-full relative order-1 md:order-2">
                        <div className="absolute -top-10 -right-10 w-40 h-40 bg-gray-100 rounded-full blur-3xl -z-10"></div>
                        <div className="relative h-[400px] md:h-[550px] w-full rounded-[2rem] overflow-hidden shadow-2xl">
                            <img
                                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2053&auto=format&fit=crop"
                                alt="Hamptons Luxury Home"
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.5s] ease-out"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- OWNER TESTIMONIALS (DINAMICI ORA) --- */}
            <section className="bg-gray-50 py-16 md:py-24 rounded-t-[3rem] -mt-10 md:-mt-16 relative z-0">
                <div className="max-w-[1400px] mx-auto px-4 md:px-6">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-12">
                        <div className="text-center md:text-left w-full md:w-auto">
                            <span className="text-brand font-bold text-xs md:text-sm tracking-widest uppercase">For Owners</span>
                            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mt-2">Why Owners Trust Us</h2>
                        </div>

                        <div className="hidden md:flex gap-3 mt-4 md:mt-0">
                            <button onClick={() => scroll('left')} className="p-3 rounded-full bg-white border border-gray-200 hover:bg-brand hover:border-brand hover:text-white transition-all shadow-sm">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button onClick={() => scroll('right')} className="p-3 rounded-full bg-white border border-gray-200 hover:bg-brand hover:border-brand hover:text-white transition-all shadow-sm">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Scrollable Container */}
                    <div
                        ref={scrollContainerRef}
                        className="flex overflow-x-auto gap-6 pb-10 snap-x snap-mandatory scrollbar-hide px-2 -mx-2"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {/* 3. MAPPING SULLA VARIABILE DI STATO 'testimonials' INVECE CHE SU 'OWNER_TESTIMONIALS' */}
                        {testimonials.length > 0 ? (
                            testimonials.map((item) => (
                                <div
                                    key={item.id}
                                    className="flex-none w-[85vw] md:w-[400px] snap-center bg-white p-8 rounded-2xl shadow-lg border border-gray-100 flex flex-col hover:shadow-xl transition-shadow duration-300"
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-2 bg-gray-50 rounded-lg">
                                            <Building2 className="w-6 h-6 text-gray-400" />
                                        </div>
                                        <Quote className="w-8 h-8 text-brand opacity-20" />
                                    </div>

                                    <div className="flex mb-4">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} className={`w-4 h-4 ${i < item.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                                        ))}
                                    </div>

                                    <p className="text-gray-600 mb-6 italic leading-relaxed line-clamp-3 flex-grow">
                                        "{item.preview}"
                                    </p>

                                    <div className="mt-auto pt-6 border-t border-gray-100">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                {/* Adattamento campi: authorName e authorRole */}
                                                <h4 className="font-bold text-gray-900 text-sm">{item.authorName}</h4>
                                                <p className="text-xs text-brand font-medium">{item.authorRole}</p>
                                            </div>
                                            <button
                                                onClick={() => setSelectedTestimonial(item)}
                                                className="text-xs font-bold text-gray-900 border-b-2 border-gray-200 hover:border-brand transition-colors pb-0.5"
                                            >
                                                Read Story
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            // Placeholder nel caso non ci siano ancora recensioni caricate
                            <div className="flex-none w-[85vw] md:w-[400px] snap-center p-8 text-center text-gray-500 italic">
                                Loading testimonials...
                            </div>
                        )}

                        {/* Call to Action Card in Slider */}
                        <div className="flex-none w-[85vw] md:w-[400px] snap-center bg-gray-900 p-8 rounded-2xl shadow-lg flex flex-col justify-center items-center text-center text-white">
                            <Wallet className="w-12 h-12 mb-4 text-brand" />
                            <h3 className="text-xl font-bold mb-2">Own a property?</h3>
                            <p className="text-gray-300 mb-6 text-sm">Join our exclusive collection and turn your property into a high-yield asset.</p>
                            <button
                                onClick={() => navigate('contact')}
                                className="bg-brand px-6 py-2 rounded-full font-bold text-sm hover:bg-white hover:text-gray-900 transition-colors"
                            >
                                Become a Partner
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- CORE VALUES --- (Invariato) */}
            <section className="py-12 md:py-16 bg-white">
                <div className="max-w-[1400px] mx-auto px-4 md:px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                        {/* Seamless Access */}
                        <div className="flex flex-col items-center text-center p-4 md:p-6">
                            <div className="p-4 rounded-full mb-4 md:mb-6" style={{ backgroundColor: `${BRAND_CORAL}1A` }}>
                                <Smartphone className="w-6 h-6 md:w-8 md:h-8" style={{ color: BRAND_CORAL }} />
                            </div>
                            <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">Seamless Access</h3>
                            <p className="text-sm md:text-base text-gray-500 leading-relaxed">Check-in and access your home directly from your smartphone.</p>
                        </div>
                        {/* Premium Standards */}
                        <div className="flex flex-col items-center text-center p-4 md:p-6">
                            <div className="p-4 rounded-full mb-4 md:mb-6" style={{ backgroundColor: `${BRAND_CORAL}1A` }}>
                                <ShieldCheck className="w-6 h-6 md:w-8 md:h-8" style={{ color: BRAND_CORAL }} />
                            </div>
                            <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">Premium Standards</h3>
                            <p className="text-sm md:text-base text-gray-500 leading-relaxed">Personally inspected and maintained properties.</p>
                        </div>
                        {/* Always Connected */}
                        <div className="flex flex-col items-center text-center p-4 md:p-6">
                            <div className="p-4 rounded-full mb-4 md:mb-6" style={{ backgroundColor: `${BRAND_CORAL}1A` }}>
                                <Clock className="w-6 h-6 md:w-8 md:h-8" style={{ color: BRAND_CORAL }} />
                            </div>
                            <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">Always Connected</h3>
                            <p className="text-sm md:text-base text-gray-500 leading-relaxed">High-speed Wi-Fi and 24/7 digital concierge.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- MODAL FOR TESTIMONIALS (Aggiornato con i nuovi campi) --- */}
            {selectedTestimonial && createPortal(
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
                    onClick={() => setSelectedTestimonial(null)}
                >
                    <div
                        className="bg-white rounded-3xl p-8 md:p-12 max-w-2xl w-full shadow-2xl relative animate-zoom-in max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setSelectedTestimonial(null)}
                            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
                        >
                            <X className="w-6 h-6 text-gray-500" />
                        </button>

                        <div className="flex items-center gap-4 mb-6 mt-2">
                            <div className="w-14 h-14 rounded-full bg-brand/10 flex-shrink-0 flex items-center justify-center text-brand font-bold text-xl">
                                {selectedTestimonial.authorName.charAt(0)}
                            </div>
                            <div>
                                <h3 className="text-xl md:text-2xl font-serif font-bold text-gray-900">{selectedTestimonial.authorName}</h3>
                                <p className="text-gray-500 text-sm">{selectedTestimonial.authorRole}</p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <Quote className="w-8 h-8 text-brand mb-4 opacity-30" />
                            <p className="text-gray-700 text-lg md:text-xl leading-relaxed italic font-serif">
                                {/* Usiamo fullText (che è il content del backend) */}
                                "{selectedTestimonial.fullText}"
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-5 h-5 ${i < selectedTestimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                            ))}
                            <span className="text-gray-400 text-sm ml-2">Verified Owner</span>
                        </div>
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default HomeContent;